/**
 * CommandPalette — global ⌘K / Ctrl-K search across the workspace.
 *
 * Mounts at the app shell level. Listens for ⌘K (mac) / Ctrl-K
 * (win/linux) at the document level. Opens a centered modal with a
 * search input + a fans-out result list across whatever entity types
 * the host's search endpoint returns.
 *
 * Debounced query (250ms) → fetch /api/v1/workspaces/:wsId/search?q=…
 * → render grouped results with keyboard navigation (↑↓ + Enter).
 *
 * The host app is responsible for:
 *   - Mounting <CommandPalette workspaceId={...} /> once in the shell
 *   - Optionally passing csrfHeader for protected backends
 *
 * The framework owns the keyboard hotkey, the modal chrome, and the
 * fetch + result rendering. Search semantics live server-side.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, Circle, FileText, Landmark, Search, Target, UserRound, type LucideIcon } from "lucide-react";

interface SearchResult {
  kind: string;
  id: string;
  title: string;
  subtitle?: string | null;
  href: string;
}

interface SearchResponse {
  q: string;
  results: SearchResult[];
  by_kind: Record<string, number>;
}

export interface CommandPaletteProps {
  /** Workspace whose entities to search. Pass null/undefined when no
   *  workspace is selected — the palette renders disabled. */
  workspaceId: string | null | undefined;
  /** Optional CSRF header builder (the search endpoint runs csrfProtect). */
  csrfHeader?: () => Record<string, string>;
}

const KIND_ICON: Record<string, LucideIcon> = {
  bill: FileText,
  member: Landmark,
  contact: UserRound,
  account: Building2,
  issue: Target,
};

const KIND_LABEL: Record<string, string> = {
  bill: "Bill",
  member: "Member",
  contact: "Contact",
  account: "Org",
  issue: "Issue",
};

export function CommandPalette({ workspaceId, csrfHeader: _csrfHeader }: CommandPaletteProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // ⌘K / Ctrl-K → open. Esc → close.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isOpenKey = (e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey);
      if (isOpenKey) {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  // Reset state on close, focus input on open.
  useEffect(() => {
    if (open) {
      setQuery("");
      setDebounced("");
      setActiveIdx(0);
      // Defer focus to the next paint so the modal is in the DOM.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Debounce query input by 250ms — typing fast doesn't fire 12 fetches.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const { data, isFetching } = useQuery<SearchResponse>({
    queryKey: ["command-palette", workspaceId, debounced],
    enabled: !!workspaceId && debounced.length >= 2,
    queryFn: async () => {
      const res = await fetch(
        `/api/v1/workspaces/${encodeURIComponent(workspaceId!)}/search?q=${encodeURIComponent(debounced)}`,
        { credentials: "include" }
      );
      if (!res.ok) return { q: debounced, results: [], by_kind: {} };
      return res.json();
    },
    staleTime: 30_000,
  });

  const results = useMemo(() => data?.results ?? [], [data]);

  // Reset active row when results change.
  useEffect(() => {
    setActiveIdx(0);
  }, [debounced, results.length]);

  // Scroll active row into view.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  const navigate = useCallback((href: string) => {
    setOpen(false);
    // Use full navigation rather than react-router to keep the framework
    // router-agnostic. Host apps that want SPA navigation can replace
    // window.location.href with their router's push.
    window.location.href = href;
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(0, results.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && results[activeIdx]) {
      e.preventDefault();
      navigate(results[activeIdx].href);
    }
  };

  // Group results by kind for the rendered list.
  const grouped = useMemo(() => {
    const map = new Map<string, SearchResult[]>();
    for (const r of results) {
      if (!map.has(r.kind)) map.set(r.kind, []);
      map.get(r.kind)!.push(r);
    }
    return Array.from(map.entries());
  }, [results]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-start justify-center pt-[15vh] bg-stone-900/40 backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-[min(640px,calc(100vw-32px))] bg-white border border-stone-200 rounded-xl shadow-overlay overflow-hidden flex flex-col max-h-[70vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-100">
          <Search size={16} className="text-stone-400 shrink-0" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={
              workspaceId
                ? "Search bills, members, contacts, orgs, issues…"
                : "Pick a workspace first to search."
            }
            disabled={!workspaceId}
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-stone-400 disabled:opacity-50"
          />
          {isFetching && (
            <span className="text-[11px] text-stone-400 italic">searching…</span>
          )}
          <kbd className="text-[10px] text-stone-400 font-mono border border-stone-200 px-1.5 py-0.5 rounded">
            esc
          </kbd>
        </div>
        <div ref={listRef} className="overflow-y-auto flex-1">
          {!workspaceId && (
            <div className="px-4 py-8 text-center text-xs text-stone-400 italic">
              Pick a workspace to search across its bills, members, contacts, orgs, and issues.
            </div>
          )}
          {workspaceId && debounced.length < 2 && (
            <div className="px-4 py-8 text-center text-xs text-stone-400 italic">
              Type at least 2 characters to search.
            </div>
          )}
          {workspaceId && debounced.length >= 2 && results.length === 0 && !isFetching && (
            <div className="px-4 py-8 text-center text-xs text-stone-500">
              No matches for <span className="font-mono">{debounced}</span>.
            </div>
          )}
          {grouped.map(([kind, items]) => (
            <div key={kind} className="px-2 py-1.5">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 px-2 py-1">
                {KIND_LABEL[kind] ?? kind}
                <span className="ml-1.5 text-stone-300 font-mono">·{items.length}</span>
              </div>
              {items.map((r) => {
                const idx = results.indexOf(r);
                const active = idx === activeIdx;
                const KindIcon = KIND_ICON[kind] ?? Circle;
                return (
                  <div
                    key={`${kind}:${r.id}`}
                    data-idx={idx}
                    onClick={() => navigate(r.href)}
                    onMouseEnter={() => setActiveIdx(idx)}
                    className={`flex items-center gap-2.5 px-2 py-1.5 rounded cursor-pointer ${active ? "bg-stone-100" : "hover:bg-stone-50"}`}
                  >
                    <span
                      className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${active ? "bg-accent/70 text-ink" : "bg-stone-100 text-stone-500"}`}
                      aria-hidden
                    >
                      <KindIcon size={14} strokeWidth={1.75} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-stone-900 truncate">{r.title}</div>
                      {r.subtitle && (
                        <div className="text-[11px] text-stone-500 truncate">{r.subtitle}</div>
                      )}
                    </div>
                    {active && (
                      <kbd className="text-[10px] text-stone-400 font-mono border border-stone-200 px-1 rounded">
                        ↵
                      </kbd>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="px-4 py-2 border-t border-stone-100 text-[11px] text-stone-400 flex items-center gap-3 bg-stone-50/50">
          <span className="flex items-center gap-1">
            <kbd className="font-mono border border-stone-200 px-1 rounded bg-white">↑↓</kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="font-mono border border-stone-200 px-1 rounded bg-white">↵</kbd>
            open
          </span>
          <span className="flex items-center gap-1">
            <kbd className="font-mono border border-stone-200 px-1 rounded bg-white">esc</kbd>
            close
          </span>
          <span className="ml-auto">
            <kbd className="font-mono border border-stone-200 px-1 rounded bg-white">⌘K</kbd>
            to toggle anywhere
          </span>
        </div>
      </div>
    </div>
  );
}
